import { Button } from "@b2b/ui-components";
import { Package, ShoppingCart, Check } from "lucide-react";
import type { TradeRole } from "../hooks/useRegistrationForm";

export interface RoleSelectionStepProps {
  value: TradeRole;
  onChange: (role: TradeRole) => void;
  onContinue: () => void;
}

const roles: {
  value: TradeRole;
  title: string;
  subtitle: string;
  bullets: string[];
  icon: typeof Package;
}[] = [
  {
    value: "supplier",
    title: "Sell",
    subtitle: "I have products and I need more buyers",
    bullets: [
      "List your products for free",
      "Get matched with buying requests",
      "Receive inquiries from buyers",
      "Send quotes and close deals",
    ],
    icon: Package,
  },
  {
    value: "buyer",
    title: "Buy",
    subtitle: "I need to find suppliers or source products",
    bullets: [
      "Search 10,000+ verified suppliers",
      "Post what you're looking for",
      "Receive and compare quotes",
      "Contact suppliers directly",
    ],
    icon: ShoppingCart,
  },
];

const benefits = [
  "Join 10,000+ verified suppliers",
  "No credit card required",
  "Free to start",
];

export const RoleSelectionStep = ({
  value,
  onChange,
  onContinue,
}: RoleSelectionStepProps) => {
  return (
    <div className="w-full min-w-0 space-y-6 text-center sm:space-y-8">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-500">Step 1 of 2</p>
        <div className="mx-auto mt-2 h-2 w-full max-w-[16rem] overflow-hidden rounded-full bg-gray-200 sm:max-w-xs">
          <div
            className="h-full rounded-full bg-orange-500 transition-all"
            style={{ width: "50%" }}
          />
        </div>
      </div>

      <div className="min-w-0 space-y-2 px-1">
        <h3 className="text-lg font-semibold text-gray-900 sm:text-2xl">
          What do you want to achieve with BizCoverIndia?
        </h3>
        <p className="text-sm text-gray-600 sm:text-base">
          Let's get you set up in under 2 minutes. Choose what you need most
          right now.
        </p>
      </div>

      <div className="grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        {roles.map((role) => {
          const Icon = role.icon;
          const isSelected = value === role.value;
          return (
            <button
              key={role.value}
              type="button"
              onClick={() => onChange(role.value)}
              className={`relative flex min-w-0 flex-col rounded-2xl border-2 p-4 text-left shadow-sm transition-all hover:border-orange-200 sm:p-6 ${
                isSelected
                  ? "border-orange-500 bg-orange-50/40"
                  : "border-gray-200 bg-white"
              }`}
              aria-pressed={isSelected}
              aria-label={`Select ${role.title}: ${role.subtitle}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 sm:h-12 sm:w-12">
                  <Icon className="h-5 w-5 text-orange-600 sm:h-6 sm:w-6" />
                </div>
                <div
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 sm:h-6 sm:w-6 ${
                    isSelected
                      ? "border-gray-900 bg-gray-900"
                      : "border-gray-300 bg-white"
                  }`}
                  aria-hidden
                >
                  {isSelected && (
                    <Check
                      className="h-3 w-3 text-white sm:h-3.5 sm:w-3.5"
                      strokeWidth={3}
                    />
                  )}
                </div>
              </div>
              <h4 className="mt-4 text-base font-semibold text-gray-900 sm:mt-5 sm:text-lg">
                {role.title}
              </h4>
              <p className="mt-1.5 text-sm text-gray-600 sm:mt-2 sm:text-base">
                {role.subtitle}
              </p>
              <ul className="mt-4 space-y-2 sm:mt-5 sm:space-y-3">
                {role.bullets.map((bullet, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-gray-700 sm:gap-3 sm:text-base"
                  >
                    <span className="mt-1 shrink-0 text-orange-500">•</span>
                    <span className="min-w-0 break-words">{bullet}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-4 border-t border-gray-200 pt-6 sm:gap-6 sm:pt-8 md:flex-row md:items-center md:justify-center">
        <ul className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-gray-600 sm:gap-x-6 sm:text-base md:gap-x-8">
          {benefits.map((benefit, i) => (
            <li key={i} className="flex items-center gap-2">
              <Check className="h-4 w-4 shrink-0 text-green-600 sm:h-5 sm:w-5" />
              <span className="whitespace-nowrap">{benefit}</span>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={onContinue}
          className="w-full md:w-auto md:shrink-0"
        >
          Continue →
        </Button>
      </div>
    </div>
  );
};
